"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DatePicker from "@/components/form/date-picker";
import Pagination from "@/components/tables/Pagination";
import DrawerPanel from "@/components/ui/DrawerPanel";
import { Modal } from "@/components/ui/modal/index";
import ReceiptForm from "@/components/forms/ReceiptForm";
import { TrashBinIcon } from "@/icons";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

const PAGE_SIZE = 20;

export default function RmcReceipts() {
  const qc = useQueryClient();
  const { isoToday, isoThreeDaysAgo } = React.useMemo(() => {
    const now = new Date();
    const fromDate = new Date(now);
    fromDate.setDate(fromDate.getDate() - 3);
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const fromY = fromDate.getFullYear();
    const fromM = String(fromDate.getMonth() + 1).padStart(2, "0");
    const fromD = String(fromDate.getDate()).padStart(2, "0");
    return {
      isoToday: `${y}-${m}-${d}`,
      isoThreeDaysAgo: `${fromY}-${fromM}-${fromD}`,
    };
  }, []);
  const [open, setOpen] = React.useState(false);
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [filters, setFilters] = React.useState({
    date_from: isoThreeDaysAgo,
    date_to: isoToday,
    party_id: "",
  });
  const [page, setPage] = React.useState(1);
  const [isExporting, setIsExporting] = React.useState(false);
  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });
  const queryParams = React.useMemo(
    () => ({
      ...(filters.party_id ? { party_id: filters.party_id } : {}),
      ...(filters.date_from ? { date_from: filters.date_from } : {}),
      ...(filters.date_to ? { date_to: filters.date_to } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [filters, page]
  );
  const { data: receiptsData, isFetching } = useQuery({
    queryKey: ["receipts", queryParams],
    queryFn: async () => (await rmcClient.listReceipts(queryParams)).data,
    keepPreviousData: true,
  });
  const rows = React.useMemo(() => {
    if (Array.isArray(receiptsData)) return receiptsData;
    if (Array.isArray(receiptsData?.results)) return receiptsData.results;
    if (Array.isArray(receiptsData?.data?.results)) return receiptsData.data.results;
    return [];
  }, [receiptsData]);
  const totalCount = React.useMemo(() => {
    const value = Number(receiptsData?.count);
    if (Number.isFinite(value) && value >= 0) return value;
    const nested = Number(receiptsData?.data?.count);
    if (Number.isFinite(nested) && nested >= 0) return nested;
    return rows.length;
  }, [receiptsData, rows.length]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const activeFilterCount = React.useMemo(
    () => [filters.date_from, filters.date_to, filters.party_id].filter(Boolean).length,
    [filters]
  );

  const pn = React.useCallback((id) => parties.find((p) => p.id === id)?.name || "—", [parties]);
  const updateFilters = React.useCallback((next) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  }, []);
  const clearFilters = React.useCallback(() => {
    setFilters({
      date_from: isoThreeDaysAgo,
      date_to: isoToday,
      party_id: "",
    });
    setPage(1);
  }, [isoThreeDaysAgo, isoToday]);
  const exportFiltered = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const payload = (
        await rmcClient.listReceipts({
          ...(filters.party_id ? { party_id: filters.party_id } : {}),
          ...(filters.date_from ? { date_from: filters.date_from } : {}),
          ...(filters.date_to ? { date_to: filters.date_to } : {}),
          pagination: false,
        })
      ).data;
      const exportRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data?.results)
            ? payload.data.results
            : [];
      const header = ["receipt_no", "date", "party", "amount", "remark"];
      const lines = exportRows.map((r) =>
        [r.receipt_no ?? "", r.date ?? "", pn(r.party_id), r.amount ?? "", r.remark ?? ""]
          .map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`)
          .join(",")
      );
      const csv = [header.join(","), ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${exportRows.length} receipts`);
    } catch {
      toast.error("Failed to export receipts.");
    } finally {
      setIsExporting(false);
    }
  }, [filters, pn]);

  const remove = useMutation({
    mutationFn: (id) => rmcClient.deleteReceipt(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["receipts"] });
      setDeleteRow(null);
      toast.success("Receipt deleted");
    },
    onError: () => toast.error("Failed to delete receipt"),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageBreadCrumb pageTitle="Receipts" />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isExporting}
            onClick={exportFiltered}
          >
            {isExporting ? "Exporting…" : "Export All Filtered"}
          </Button>
          <Button size="sm" onClick={() => setOpen(true)}>
            New Receipt
          </Button>
        </div>
      </div>
      <div className="rounded-2xl border border-gray-200/80 bg-white/90 p-4 shadow-lg shadow-gray-200/50 ring-1 ring-gray-100/80 backdrop-blur-sm transition-all dark:border-gray-800 dark:bg-gray-900/50 dark:shadow-black/30 dark:ring-gray-800/80">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Smart Filters
            </p>
            {activeFilterCount > 0 && (
              <span className="menu-dropdown-badge-active rounded-full px-2.5 py-1 text-[11px] font-semibold">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {rows.length} of {totalCount} records
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date From</label>
            <DatePicker
              id="receipt-filter-from"
              defaultDate={filters.date_from}
              placeholder="From date"
              onChange={(_, dateStr) => updateFilters({ date_from: dateStr || "" })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date To</label>
            <DatePicker
              id="receipt-filter-to"
              defaultDate={filters.date_to}
              placeholder="To date"
              onChange={(_, dateStr) => updateFilters({ date_to: dateStr || "" })}
            />
          </div>
          <SearchableSelect
            label="Party"
            value={filters.party_id}
            onChange={(v) => updateFilters({ party_id: v || "" })}
            options={[{ id: "", name: "All parties" }, ...parties]}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            getSubLabel={(p) => p.gst_number}
            placeholder="All parties"
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200/70 pt-3 dark:border-gray-800">
          <Button type="button" size="sm" variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </div>
      <DataTable
        columns={[
          { key: "no", header: "Receipt No", render: (r) => r.receipt_no },
          { key: "date", header: "Date" },
          { key: "party", header: "Party", render: (r) => pn(r.party_id) },
          { key: "amt", header: "Amount", render: (r) => `₹${r.amount}` },
          { key: "rm", header: "Remark", render: (r) => r.remark || "—" },
          {
            key: "act",
            header: "",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (r) => (
              <div className="flex justify-end">
                <button
                  type="button"
                  aria-label={`Delete receipt ${r.receipt_no}`}
                  title="Delete"
                  disabled={remove.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-red-500/10 dark:hover:text-red-400 disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteRow(r);
                  }}
                >
                  <TrashBinIcon className="size-4" />
                </button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={isFetching}
      />
      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
        />
      </div>
      <DrawerPanel open={open} onClose={() => setOpen(false)} title="New receipt">
        <ReceiptForm
          onDone={() => {
            setOpen(false);
            qc.invalidateQueries({ queryKey: ["receipts"] });
          }}
        />
      </DrawerPanel>
      <Modal
        isOpen={deleteRow != null}
        onClose={() => {
          if (!remove.isPending) setDeleteRow(null);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete receipt?</h3>
          <p className="text-sm text-gray-400">
            {deleteRow?.receipt_no ? (
              <>
                This will permanently remove{" "}
                <span className="font-medium text-gray-200">{deleteRow.receipt_no}</span>.
              </>
            ) : (
              "This will permanently remove the selected receipt."
            )}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={remove.isPending}
              onClick={() => setDeleteRow(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={remove.isPending}
              onClick={() => deleteRow?.id && remove.mutate(deleteRow.id)}
            >
              {remove.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
